import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  labelledBy?: string;
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  modalClassName?: string;
  /**
   * Drops the backdrop inset so the dialog covers the viewport. Used by
   * code-quiz overlays.
   */
  fullBleed?: boolean;
  /** Replaces the body's default padding, e.g. for content that paints its own panes. */
  bodyClassName?: string;
}

export const InteractiveElementOverlay = ({
  open,
  onClose,
  labelledBy,
  header,
  footer,
  children,
  modalClassName,
  fullBleed = false,
  bodyClassName,
}: Props) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => setMounted(true));
    return () => {
      cancelAnimationFrame(id);
      setMounted(false);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const backdropPadding = fullBleed ? "p-0 sm:p-0" : "p-4 sm:p-7";

  return (
    <div
      className={cn(
        "fixed inset-0 z-[100] flex items-stretch justify-center",
        backdropPadding,
        "bg-gradient-to-b from-[#07254a]/[0.42] to-[#07254a]/[0.62] backdrop-blur-[2px]",
        "transition-opacity duration-200",
        mounted ? "opacity-100" : "opacity-0"
      )}
    >
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Dialog schließen"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        className={cn(
          "relative z-10 flex w-full max-w-[960px] flex-col overflow-hidden font-sans text-[#0f2647]",
          "max-h-[calc(100vh-32px)] sm:max-h-[calc(100vh-56px)]",
          "bg-white rounded-2xl shadow-[0_30px_80px_-20px_rgba(8,30,60,0.45),0_12px_30px_-10px_rgba(8,30,60,0.25)]",
          "transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.2,0.8,0.2,1)]",
          mounted
            ? "translate-y-0 scale-100 opacity-100"
            : "translate-y-2 scale-[0.98] opacity-0",
          modalClassName
        )}
      >
        {header}
        <div
          className={cn(
            "flex-1 overflow-y-auto",
            bodyClassName ?? "px-6 py-5 sm:px-8 sm:py-6"
          )}
        >
          {children}
        </div>
        {footer}
      </div>
    </div>
  );
};
